import React, { useState, useEffect, useRef } from "react";
import { login } from "../../redux/actions/authAction";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Mobile breakpoint hook
const useIsMobile = (bp = 768) => {
  const [m, setM] = React.useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
};

// LocalStorage key for saved login credentials
const SAVED_LOGINS_KEY = "treasury_saved_logins";

function getSavedLogins() {
	try {
		return JSON.parse(localStorage.getItem(SAVED_LOGINS_KEY) || "[]");
	} catch { return []; }
}

function saveLogin(email, password) {
	const logins = getSavedLogins().filter(l => l.email !== email);
	logins.unshift({ email, password });
	// Keep only the last 5
	localStorage.setItem(SAVED_LOGINS_KEY, JSON.stringify(logins.slice(0, 5)));
}

const Login = () => {
	const { t } = useTranslation();
	const isMobile = useIsMobile();
	const [passwordShown, setPasswordShown] = useState(false);
	const [focusEmail, setFocusEmail] = useState(false);
	const [focusPass, setFocusPass] = useState(false);
	const [emailVal, setEmailVal] = useState("");
	const [passVal, setPassVal] = useState("");
	const [emailErr, setEmailErr] = useState("");
	const [passErr, setPassErr] = useState("");
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [savedLogins, setSavedLogins] = useState([]);
	const suggestionsRef = useRef(null);
	const dispatch = useDispatch();
	const { auth } = useSelector((state) => state);
	const history = useHistory();

	// Load saved logins on mount
	useEffect(() => {
		setSavedLogins(getSavedLogins());
	}, []);

	// Redirect if already logged in
	useEffect(() => {
		if (auth.data && auth.data.status === 1 && auth.data.action === "login") {
			history.push("/");
		}
	}, [auth, history]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handler = (e) => {
			if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
				setShowSuggestions(false);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	const filteredSuggestions = savedLogins.filter(l =>
		!emailVal || l.email.toLowerCase().includes(emailVal.toLowerCase())
	);

	const handleEmailChange = (e) => {
		const val = e.target.value;
		setEmailVal(val);
		setShowSuggestions(true);
		const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
		if (!val) setEmailErr(t("auth.emailRequired"));
		else if (!val.match(validRegex)) setEmailErr(t("auth.emailInvalid"));
		else setEmailErr("");
	};

	const handleSelectSuggestion = (saved) => {
		setEmailVal(saved.email);
		setPassVal(saved.password);
		setEmailErr("");
		setPassErr("");
		setShowSuggestions(false);
	};

	const handleRemoveSuggestion = (e, email) => {
		e.stopPropagation();
		const updated = savedLogins.filter(l => l.email !== email);
		localStorage.setItem(SAVED_LOGINS_KEY, JSON.stringify(updated));
		setSavedLogins(updated);
	};

	const handlePassChange = (e) => {
		const val = e.target.value;
		setPassVal(val);
			if (!val) setPassErr(t("auth.passwordRequired"));
			else setPassErr("");
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		let valid = true;
		if (!emailVal) { setEmailErr(t("auth.emailRequired")); valid = false; }
		if (!passVal) { setPassErr(t("auth.passwordRequired")); valid = false; }
		if (!valid) return;
		// Save credentials for future autofill
		saveLogin(emailVal, passVal);
		setSavedLogins(getSavedLogins());
		dispatch(login({ email: emailVal, password: passVal }));
	};

	const serverErr = auth.data?.errors?.email || auth.data?.errors?.password || (auth.data?.status === 0 && auth.data?.action !== "logout" ? auth.data?.message : "");

	/* ── Styles ── */
	const pageStyle = {
		display: "flex",
		minHeight: "100vh",
		fontFamily: "'Inter', sans-serif",
	};

	const leftPanelStyle = {
		flex: isMobile ? "1 1 auto" : "0 0 480px",
		width: isMobile ? "100%" : "480px",
		background: "#0e3725",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		padding: isMobile ? "40px 28px" : "60px 56px",
		position: "relative",
		overflow: "hidden",
	};

	const rightPanelStyle = {
		flex: 1,
		backgroundImage: "url(/theme/images/login.jpg)",
		backgroundSize: "cover",
		backgroundPosition: "center center",
		position: "relative",
		display: isMobile ? "none" : "block",
	};

	const rightOverlayStyle = {
		position: "absolute",
		inset: 0,
		background: "linear-gradient(135deg, rgba(14,55,37,0.60) 0%, rgba(0,0,0,0.25) 100%)",
	};

	const inputWrapStyle = (focused, hasErr) => ({
		position: "relative",
		borderRadius: "10px",
		border: hasErr ? "1.5px solid #ef4444" : focused ? "1.5px solid #c9a84c" : "1.5px solid rgba(255,255,255,0.18)",
		background: "rgba(255,255,255,0.07)",
		transition: "border-color 0.2s, box-shadow 0.2s",
		boxShadow: focused && !hasErr ? "0 0 0 3px rgba(201,168,76,0.18)" : "none",
	});

	const inputStyle = {
		width: "100%",
		background: "transparent",
		border: "none",
		outline: "none",
		color: "#ffffff",
		fontSize: "15px",
		padding: "14px 16px",
		fontFamily: "'Inter', sans-serif",
		boxSizing: "border-box",
	};

	const labelStyle = {
		fontSize: "11px",
		fontWeight: 700,
		letterSpacing: "0.10em",
		textTransform: "uppercase",
		color: "rgba(255,255,255,0.55)",
		marginBottom: "7px",
		display: "block",
	};

	return (
		<div style={pageStyle}>
			{/* ── Left: Form Panel ── */}
			<div style={leftPanelStyle}>
				{/* Decorative background circles */}
				<div style={{
					position: "absolute", top: -80, right: -80,
					width: 260, height: 260, borderRadius: "50%",
					background: "rgba(201,168,76,0.07)", pointerEvents: "none",
				}} />
				<div style={{
					position: "absolute", bottom: -60, left: -60,
					width: 200, height: 200, borderRadius: "50%",
					background: "rgba(201,168,76,0.05)", pointerEvents: "none",
				}} />

				{/* Logo */}
				<div style={{ marginBottom: 48 }}>
					<Link to="/" style={{ display: "inline-block" }}>
						<img src="/theme/images/logo.png" alt="Treasury" style={{ height: 40, display: "block" }} />
					</Link>
				</div>

				{/* Heading */}
				<div style={{ marginBottom: 36 }}>
					<h1 style={{
						fontFamily: "'Ibarra Real Nova', 'Georgia', serif",
						fontSize: "clamp(28px, 3vw, 36px)",
						fontWeight: 700,
						color: "#ffffff",
						margin: "0 0 10px",
						lineHeight: 1.2,
					}}>
					{t("auth.loginTitle")}
				</h1>
				<p style={{ color: "rgba(255,255,255,0.55)", fontSize: "14.5px", margin: 0, lineHeight: 1.6 }}>
					{t("auth.loginSubtitle")}
				</p>
				</div>

				{/* Server-level error */}
				{serverErr && (
					<div style={{
						background: "rgba(239,68,68,0.12)",
						border: "1px solid rgba(239,68,68,0.35)",
						borderRadius: 9,
						padding: "11px 16px",
						marginBottom: 20,
						display: "flex",
						alignItems: "center",
						gap: 8,
					}}>
						<svg width="15" height="15" fill="none" stroke="#ef4444" strokeWidth="2" viewBox="0 0 24 24">
							<circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/>
						</svg>
						<span style={{ color: "#fca5a5", fontSize: "13.5px" }}>{serverErr}</span>
					</div>
				)}

				<form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }} noValidate>
					{/* Email with recent-login dropdown */}
					<div style={{ position: "relative" }} ref={suggestionsRef}>
						<label style={labelStyle}>{t("auth.email")}</label>
						<div style={inputWrapStyle(focusEmail, !!emailErr)}>
							<div style={{ display: "flex", alignItems: "center" }}>
								<span style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "rgba(255,255,255,0.45)" }}>
									<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
										<rect x="2" y="4" width="20" height="16" rx="2"/>
										<path d="M2 7l10 7 10-7"/>
									</svg>
								</span>
								<input
									type="email"
									name="email"
									placeholder={t("auth.emailPlaceholder")}
									value={emailVal}
									onChange={handleEmailChange}
									onFocus={() => { setFocusEmail(true); if (savedLogins.length > 0) setShowSuggestions(true); }}
									onBlur={() => setFocusEmail(false)}
									style={{ ...inputStyle, paddingLeft: 8 }}
									autoComplete="off"
									autoCorrect="off"
									autoCapitalize="none"
									spellCheck={false}
								/>
								{savedLogins.length > 0 && (
									<button
										type="button"
										onClick={() => setShowSuggestions(v => !v)}
										style={{ background: "none", border: "none", cursor: "pointer", padding: "0 12px 0 4px", color: "rgba(255,255,255,0.4)", display: "flex", alignItems: "center", flexShrink: 0 }}
										tabIndex={-1}
									>
										<svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
											<path d="M7 10l5 5 5-5z"/>
										</svg>
									</button>
								)}
							</div>
						</div>
						{emailErr && (
							<span style={{ fontSize: "12px", color: "#fca5a5", marginTop: 5, display: "block" }}>{emailErr}</span>
						)}

						{/* Recent login suggestions dropdown */}
						{showSuggestions && filteredSuggestions.length > 0 && (
							<div style={{
								position: "absolute",
								top: "100%",
								left: 0,
								right: 0,
								zIndex: 100,
								background: "#0a2b1e",
								border: "1px solid rgba(201,168,76,0.3)",
								borderRadius: "10px",
								marginTop: 4,
								overflow: "hidden",
								boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
							}}>
								<div style={{ padding: "8px 14px 6px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
														{t("auth.recentLogins")}
												</div>
								{filteredSuggestions.map((saved) => (
									<div
										key={saved.email}
										onClick={() => handleSelectSuggestion(saved)}
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: "11px 14px",
											cursor: "pointer",
											borderBottom: "1px solid rgba(255,255,255,0.05)",
											transition: "background 0.15s",
										}}
										onMouseEnter={e => e.currentTarget.style.background = "rgba(201,168,76,0.08)"}
										onMouseLeave={e => e.currentTarget.style.background = "transparent"}
									>
										<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
											<div style={{
												width: 30, height: 30, borderRadius: "50%",
												background: "rgba(201,168,76,0.15)",
												display: "flex", alignItems: "center", justifyContent: "center",
												fontSize: "12px", fontWeight: 700, color: "#c9a84c",
											}}>
												{saved.email.charAt(0).toUpperCase()}
											</div>
											<span style={{ color: "#ffffff", fontSize: "14px" }}>{saved.email}</span>
										</div>
										<button
											type="button"
											onClick={(e) => handleRemoveSuggestion(e, saved.email)}
											style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", padding: 4, display: "flex", alignItems: "center" }}
											title="Remove"
										>
											<svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
												<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
											</svg>
										</button>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Password */}
					<div>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
							<label style={{ ...labelStyle, margin: 0 }}>{t("auth.password")}</label>
						</div>
						<div style={inputWrapStyle(focusPass, !!passErr)}>
							<div style={{ display: "flex", alignItems: "center" }}>
								<span style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "rgba(255,255,255,0.45)" }}>
									<svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
										<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
									</svg>
								</span>
								<input
									type={passwordShown ? "text" : "password"}
									name="password"
									placeholder={t("auth.passwordPlaceholder")}
									value={passVal}
									onChange={handlePassChange}
									onFocus={() => setFocusPass(true)}
									onBlur={() => setFocusPass(false)}
									style={{ ...inputStyle, paddingLeft: 8, paddingRight: 44 }}
									autoComplete="current-password"
								/>
								<button
									type="button"
									onClick={() => setPasswordShown(v => !v)}
									style={{
										position: "absolute", right: 14,
										background: "none", border: "none", cursor: "pointer",
										color: "rgba(255,255,255,0.45)", padding: 0, display: "flex",
									}}
									tabIndex={-1}
								>
									{passwordShown ? (
										<svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
											<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
											<path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
											<line x1="1" y1="1" x2="23" y2="23"/>
										</svg>
									) : (
										<svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
											<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
											<circle cx="12" cy="12" r="3"/>
										</svg>
									)}
								</button>
							</div>
						</div>
						{passErr && (
							<span style={{ fontSize: "12px", color: "#fca5a5", marginTop: 5, display: "block" }}>{passErr}</span>
						)}
					</div>

					{/* Submit */}
					<button
						type="submit"
						style={{
							marginTop: 8,
							width: "100%",
							padding: "15px",
							background: "linear-gradient(135deg, #c9a84c, #a07a20)",
							color: "#ffffff",
							border: "none",
							borderRadius: "10px",
							fontSize: "15px",
							fontWeight: 700,
							letterSpacing: "0.04em",
							cursor: "pointer",
							boxShadow: "0 4px 20px rgba(201,168,76,0.40)",
							transition: "transform 0.15s ease, box-shadow 0.15s ease",
							fontFamily: "'Inter', sans-serif",
						}}
						onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(201,168,76,0.55)"; }}
						onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(201,168,76,0.40)"; }}
					>
						{t("auth.loginButton")}
					</button>
				</form>

				{/* Register link */}
				<p style={{ marginTop: 28, textAlign: "center", color: "rgba(255,255,255,0.45)", fontSize: "14px" }}>
					{t("auth.noAccount")}{" "}
					<Link to="/register" style={{ color: "#c9a84c", fontWeight: 600, textDecoration: "none" }}>
						{t("auth.registerLink")}
					</Link>
				</p>

				{/* Divider line */}
				<div style={{ marginTop: "auto", paddingTop: 40, borderTop: "1px solid rgba(255,255,255,0.10)" }}>
					<p style={{ color: "rgba(255,255,255,0.25)", fontSize: "12px", margin: 0, textAlign: "center" }}>
						© {new Date().getFullYear()} Treasury. {t("footer.tokenizingSaudi")}
					</p>
				</div>
			</div>

			{/* ── Right: Hero Image Panel ── */}
			<div style={rightPanelStyle}>
				<div style={rightOverlayStyle} />
				{/* Centered tagline on the image */}
				<div style={{
					position: "absolute",
					inset: 0,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					padding: "40px",
					textAlign: "center",
				}}>
					<div style={{
						maxWidth: 420,
					}}>
						<h2 style={{
							fontFamily: "'Ibarra Real Nova', 'Georgia', serif",
							fontSize: "clamp(24px, 2.5vw, 36px)",
							fontWeight: 700,
							color: "#ffffff",
							margin: "0 0 16px",
							lineHeight: 1.25,
							textShadow: "0 2px 12px rgba(0,0,0,0.35)",
						}}>
							{t("auth.loginHeroTitle")}
						</h2>
						<p style={{
							color: "rgba(255,255,255,0.75)",
							fontSize: "15px",
							lineHeight: 1.7,
							margin: 0,
							textShadow: "0 1px 6px rgba(0,0,0,0.3)",
						}}>
							{t("auth.loginHeroSubtitle")}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Login;
